import moment from "moment";
import queryString from "query-string";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { xml2js } from "../../json/xml2js";
import {
  cloneForm,
  compareForms,
  deleteFormSubmission,
  getCareTeams,
  getClientData,
  getConfig,
  getFormsySubmissionsForClient,
  getSubmission,
  handleFormChange,
  saveForm,
  sendShareEmail,
  validateForm,
} from "../../scripts/formScripts";
import {
  ArrayCheck,
  getFieldDate,
  getFieldName,
  getFieldValue,
} from "../../scripts/helperScripts";
import BackIcon from "../common/BackIcon";
import ClientFormHeader from "../common/ClientFormHeader";
import Comment from "../common/Comment";
import { Checkbox, Input, RadioButton } from "../common/ControlBundle";
import ExpandDiv from "../common/ExpandDiv";
import FormButtonBar from "../common/FormButtonBar";
import ShareModal from "../common/ShareModal";
import Signature from "../common/Signature";
import { getSchema } from "./joi/validation";

function KYHTSA() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const helperConfiguration = useMemo(() => {
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      const defaultConfig = {
        buttonBarConfig: {
          sign: {
            display: false,
            initialSignatureType: "full",
          },
          clone: {
            display: false,
          },
          delete: {
            display: false,
          },
          share: {
            display: false,
          },
          comment: {
            display: false,
          },
        },
        signatureType: "full",
        finalSignType: "Provider",
      };

      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [rstExpandStatus, setRstExpandStatus] = useState(true);
  const [tvitExpandStatus, setTvitExpandStatus] = useState(false);

  const yesNoOptions = [
    {
      _id: "Yes",
      name: "Yes",
    },
    {
      _id: "No",
      name: "No",
    },
  ];

  const noYesOptions = [
    {
      _id: "No",
      name: "No",
    },
    {
      _id: "Yes",
      name: "Yes",
    },
  ];

  const noYesBrOptions = [
    {
      _id: "No",
      name: "No",
    },
    {
      _id: "Yes",
      name: "Yes",
    },
  ];

  const noYesDKBrOptions = [
    {
      _id: "No",
      name: "No",
    },
    {
      _id: "Yes",
      name: "Yes",
    },
    {
      _id: "Don't know",
      name: "Don't know",
    },
  ];

  const noNAYesBrOptions = [
    {
      _id: "No",
      name: "No",
    },
    {
      _id: "N/A",
      name: "N/A",
    },
    {
      _id: "Yes",
      name: "Yes",
    },
  ];

  const referralSourceOptions = [
    {
      _id: "Own agency/ internal referral",
      name: "Own agency/ internal referral",
    },
    {
      _id: "Other social service provider",
      name: "Other social service provider",
    },
    {
      _id: "Healthcare provider",
      name: "Healthcare provider",
    },
    {
      _id: "Local Police Department",
      name: "Local Police Department",
    },
    {
      _id: "Dept. of Homeland Security (DHS) / Immigration & Customs Enforcement (ICE)",
      name: "Dept. of Homeland Security (DHS) / Immigration & Customs Enforcement (ICE)",
    },
    {
      _id: "Federal Bureau of Investigation (FBI)",
      name: "Federal Bureau of Investigation (FBI)",
    },
    {
      _id: "Other law enforcement",
      name: "Other law enforcement",
    },
    {
      _id: "Referred by other client",
      name: "Referred by other client",
    },
    {
      _id: "Referred by someone else",
      name: "Referred by someone else",
    },
    {
      _id: "Walk-in",
      name: "Walk-in",
    },
  ];

  const sexOptions = [
    {
      _id: "male",
      name: "male",
    },
    {
      _id: "female",
      name: "female",
    },
    {
      _id: "not listed",
      name: "not listed",
    },
    {
      _id: "prefer not to say",
      name: "prefer not to say",
    },
  ];

  const proficiencyOptions = [
    {
      _id: "Excellent",
      name: "Excellent",
    },
    {
      _id: "Good",
      name: "Good",
    },
    {
      _id: "Fair",
      name: "Fair",
    },
    {
      _id: "Poor",
      name: "Poor",
    },
  ];

  const reasonForMigrationOptions = [
    {
      _id: "To find work",
      name: "To find work",
    },
    {
      _id: "To join family",
      name: "To join family",
    },
    {
      _id: "To join romantic partner (spouse/girlfriend/boyfriend)",
      name: "To join romantic partner (spouse/girlfriend/boyfriend)",
    },
    {
      _id: "To escape abuse by family or someone else you know",
      name: "To escape abuse by family or someone else you know",
    },
    {
      _id: "To escape conflict/violence/persecution",
      name: "To escape conflict/violence/persecution",
    },
    {
      _id: "Other",
      name: "Other",
    },
  ];

  const ageGroupOptions = [
    {
      _id: "under 12",
      name: "under 12",
    },
    {
      _id: "13-17",
      name: "13-17",
    },
    {
      _id: "18-19",
      name: "18-19",
    },
    {
      _id: "20-24",
      name: "20-24",
    },
    {
      _id: "25-29",
      name: "25-29",
    },
    {
      _id: "30-34",
      name: "30-34",
    },
    {
      _id: "35-39",
      name: "35-39",
    },
    {
      _id: "40-44",
      name: "40-44",
    },
    {
      _id: "45-49",
      name: "45-49",
    },
    {
      _id: "50-54",
      name: "50-54",
    },
    {
      _id: "55-59",
      name: "55-59",
    },
    {
      _id: "60+",
      name: "60+",
    },
  ];

  const yearsInSchoolOptions = [
    {
      _id: "1-6 years",
      name: "1-6 years",
    },
    {
      _id: "7-12 years",
      name: "7-12 years",
    },
    {
      _id: "More than 12 years",
      name: "More than 12 years",
    },
    {
      _id: "Other",
      name: "Other",
    },
  ];

  const arrivalTimeOptions = [
    {
      _id: "Less than 1 year",
      name: "Less than 1 year",
    },
    {
      _id: "1 year",
      name: "1 year",
    },
    {
      _id: "2 years",
      name: "2 years",
    },
    {
      _id: "3 years",
      name: "3 years",
    },
    {
      _id: "4 years",
      name: "4 years",
    },
    {
      _id: "5 to 10 years",
      name: "5 to 10 years",
    },
    {
      _id: "More than 10 years",
      name: "More than 10 years",
    },
  ];

  const livingSituationOptions = [
    {
      _id: "By yourself",
      name: "By yourself",
    },
    {
      _id: "With your family",
      name: "With your family",
    },
    {
      _id: "With others",
      name: "With others",
    },
  ];

  const likelihoodOptions = [
    {
      _id: "certainly not",
      name: "certainly not",
    },
    {
      _id: "likely not",
      name: "likely not",
    },
    {
      _id: "uncertain either way",
      name: "uncertain either way",
    },
    {
      _id: "likely",
      name: "likely",
    },
    {
      _id: "certainly",
      name: "certainly",
    },
  ];

  const handleSubmitRST = () => {
    const errs = { ...errors };
    if (data.RSTTransported["@value"] === undefined)
      errs.RSTTransported = `"RSTTransported" is required.`;
    if (data.RSTPromises["@value"] === undefined)
      errs.RSTPromises = `"RSTPromises" is required.`;
    if (data.RSTLegalStatus["@value"] === undefined)
      errs.RSTLegalStatus = `"RSTLegalStatus" is required.`;
    if (data.RSTBought["@value"] === undefined)
      errs.RSTBought = `"RSTBought" is required.`;
    if (data.RSTHarm["@value"] === undefined)
      errs.RSTHarm = `"RSTHarm" is required.`;
    if (data.RSTDistrust["@value"] === undefined)
      errs.RSTDistrust = `"RSTDistrust" is required.`;
    if (data.RSTDocuments["@value"] === undefined)
      errs.RSTDocuments = `"RSTDocuments" is required.`;
    if (data.RSTIsolated["@value"] === undefined)
      errs.RSTIsolated = `"RSTIsolated" is required.`;
    if (data.RSTNoPayment["@value"] === undefined)
      errs.RSTNoPayment = `"RSTNoPayment" is required.`;
    if (data.RSTLabor["@value"] === undefined)
      errs.RSTLabor = `"RSTLabor" is required.`;
    if (data.RSTEvidence["@value"] === undefined)
      errs.RSTEvidence = `"RSTEvidence" is required.`;
    if (data.RSTDebt["@value"] === undefined)
      errs.RSTDebt = `"RSTDebt" is required.`;
    setErrors(errs);

    if (Object.keys(errs).length === 0) {
      let yesCount = 0;

      if (data.RSTTransported["@value"] === "Yes") yesCount++;
      if (data.RSTPromises["@value"] === "Yes") yesCount++;
      if (data.RSTLegalStatus["@value"] === "Yes") yesCount++;
      if (data.RSTBought["@value"] === "Yes") yesCount++;
      if (data.RSTHarm["@value"] === "Yes") yesCount++;
      if (data.RSTDistrust["@value"] === "Yes") yesCount++;
      if (data.RSTDocuments["@value"] === "Yes") yesCount++;
      if (data.RSTIsolated["@value"] === "Yes") yesCount++;
      if (data.RSTNoPayment["@value"] === "Yes") yesCount++;
      if (data.RSTLabor["@value"] === "Yes") yesCount++;
      if (data.RSTEvidence["@value"] === "Yes") yesCount++;
      if (data.RSTDebt["@value"] === "Yes") yesCount++;

      const obj = { ...data };
      obj.RSTCompleted["@value"] = "true";
      obj.RSTTVIT["@value"] = yesCount >= 2 ? "true" : "false";
      setData(obj);

      setRstExpandStatus(false);
      setTvitExpandStatus(yesCount >= 2);
    }
  };

  ////UI
  return (
    <>
      <div className="mb-3">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-3 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <ExpandDiv
                id="RapidScreeningTool"
                className="mt-2"
                title="Rapid Screening Tool (RST)"
                expandStatus={rstExpandStatus}
              >
                <h5>Process-Action</h5>
                <div className="container">
                  <b>
                    <i>
                      Potential victim was or is currently being recruited,
                      enticed, induced, harbored, transported, or obtained by
                      family member, stranger, employer, or acquaintance.
                    </i>
                  </b>
                  <div className="container">
                    <div className="row">
                      <div className="col-2 py-2">
                        <RadioButton
                          name="RSTTransported"
                          error={errors.RSTTransported}
                          value={data.RSTTransported["@value"]}
                          options={yesNoOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col py-2">
                        Child has been accompanined OR transported to current
                        location from anywhere in the U.S. or from another
                        country
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-2 py-2">
                        <RadioButton
                          name="RSTPromises"
                          error={errors.RSTPromises}
                          value={data.RSTPromises["@value"]}
                          options={yesNoOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col py-2">
                        Child has been promised things (e.g. job, payment,
                        housing school legal status, improved
                        circumstances/better life) in exchange for movement from
                        one residence/community/city/state/country to another
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-2 py-2">
                        <RadioButton
                          name="RSTLegalStatus"
                          error={errors.RSTLegalStatus}
                          value={data.RSTLegalStatus["@value"]}
                          options={yesNoOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col py-2">
                        Child is being kept or has been kept in someone's home
                        or place of business without (or with undetermined)
                        legal status, or lives with employer
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-2 py-2">
                        <RadioButton
                          name="RSTBought"
                          error={errors.RSTBought}
                          value={data.RSTBought["@value"]}
                          options={yesNoOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col py-2">
                        Child appears to have been "bought" or "sold"
                      </div>
                    </div>
                    {data.RSTBought["@value"] === "Yes" ? (
                      <i>
                        *Due to the possibility of child being "bought" or
                        "sold", please consult with supervisor for next steps
                      </i>
                    ) : null}
                  </div>
                </div>
                <hr />
                <h5>Means</h5>
                <div className="container">
                  <b>
                    <i>
                      Potential victim has suffered physical harm, physical
                      restraint, abuse of legal process, withholding or control
                      of identification documents, financial harm/control,
                      enticement, coercion, verbal threats, threats to harm
                      (physically or financially) family members, scheme or
                      plan, intimidation.
                    </i>
                  </b>
                  <div className="container">
                    <div className="row">
                      <div className="col-2 py-2">
                        <RadioButton
                          name="RSTHarm"
                          error={errors.RSTHarm}
                          value={data.RSTHarm["@value"]}
                          options={yesNoOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col py-2">
                        Evidence of physical harm OR threats of harm to child or
                        child's family or friends
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-2 py-2">
                        <RadioButton
                          name="RSTDistrust"
                          error={errors.RSTDistrust}
                          value={data.RSTDistrust["@value"]}
                          options={yesNoOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col py-2">
                        Child told to distrust authority figures
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-2 py-2">
                        <RadioButton
                          name="RSTDocuments"
                          error={errors.RSTDocuments}
                          value={data.RSTDocuments["@value"]}
                          options={yesNoOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col py-2">
                        Identification documents (legitmate or fraudulent) have
                        been taken away or manipulated
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-2 py-2">
                        <RadioButton
                          name="RSTIsolated"
                          error={errors.RSTIsolated}
                          value={data.RSTIsolated["@value"]}
                          options={yesNoOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col py-2">
                        Child is isolated (from family, friends, or community)
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-2 py-2">
                        <RadioButton
                          name="RSTNoPayment"
                          error={errors.RSTNoPayment}
                          value={data.RSTNoPayment["@value"]}
                          options={yesNoOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col py-2">
                        Child is not receiving payment for employment or
                        servicexs, or has "quota" of money to be earned for
                        labor (or sexual) services, or not in control of money
                        earned, or broken promises over type of work (sexual or
                        label) engaged in
                      </div>
                    </div>
                  </div>
                </div>
                <hr />
                <h5>Purpose</h5>
                <div className="container">
                  <b>
                    <i>
                      Potential victim has been forced into involuntary
                      servitude, commerical sexual activity, debt bondage, or
                      forced labor.
                    </i>
                  </b>
                  <div className="container">
                    <div className="row">
                      <div className="col-2 py-2">
                        <RadioButton
                          name="RSTLabor"
                          error={errors.RSTLabor}
                          value={data.RSTLabor["@value"]}
                          options={yesNoOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col py-2">
                        Child has engaged in any labor or services (e.g. retail,
                        factory, farm, household, babysitting, cooking,
                        restaurant, hotel, massage/spa, construction, exotic
                        dancing, etc.)
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-2 py-2">
                        <RadioButton
                          name="RSTEvidence"
                          error={errors.RSTEvidence}
                          value={data.RSTEvidence["@value"]}
                          options={yesNoOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col py-2">
                        Evidence of prostitution or pornography
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-2 py-2">
                        <RadioButton
                          name="RSTDebt"
                          error={errors.RSTDebt}
                          value={data.RSTDebt["@value"]}
                          options={yesNoOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col py-2">
                        Child owes a debt (for any reason)
                      </div>
                    </div>
                    {data.RSTEvidence["@value"] === "Yes" ? (
                      <i>
                        *Due to evidence of prostituion and/or pornography,
                        please consult with supervisor for next steps
                      </i>
                    ) : null}
                  </div>
                </div>
                {data.RSTCompleted["@value"] !== "true" ? (
                  <>
                    <div
                      className="btn btn-primary m-2 d-print-none"
                      onClick={() => {
                        handleSubmitRST();
                      }}
                    >
                      Submit RST
                    </div>
                    {errors.RSTTVIT && (
                      <div className="alert alert-danger">
                        Please submit the Rapid Screening Tool
                      </div>
                    )}
                  </>
                ) : null}
                {data.RSTTVIT["@value"] === "false" ? (
                  <>No further questions.</>
                ) : null}
              </ExpandDiv>
              {data.RSTTVIT["@value"] === "true" ? (
                <>
                  Since you answered "yes" to two or more of the screener
                  questions, there is a good chance human trafficking is
                  involved. Please complete the Trafficking Victim
                  Identification Tool (TVIT) below
                  <ExpandDiv
                    id="TVIT"
                    className="mt-2"
                    title="Trafficking Victim Identification Tool (TVIT) Long Version"
                    expandStatus={tvitExpandStatus}
                  >
                    <div className="h5 border p-2">
                      Section 1: Screening Background{" "}
                      <i>[DO NOT READ TO CLIENT]</i>
                    </div>
                    <div className="container">
                      <Input
                        name="InterviewDate"
                        label="1a. Date of interview:"
                        error={errors.InterviewDate}
                        value={data.InterviewDate["@value"]}
                        onChange={handleChange}
                        identical={compare("InterviewDate")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        type="date"
                      />
                      <RadioButton
                        name="ReferredHow"
                        label="1b. How client was referred to your agency most recently:"
                        error={errors.ReferredHow}
                        value={data.ReferredHow["@value"]}
                        options={referralSourceOptions}
                        optionsOnNewLine={true}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      {data.ReferredHow["@value"] ===
                        "Other social service provider" ||
                      data.ReferredHow["@value"] === "Other law enforcement" ||
                      data.ReferredHow["@value"] ===
                        "Referred by someone else" ? (
                        <Input
                          name="OtherReferralDetails"
                          label={`${data.ReferredHow["@value"]} name/details:`}
                          error={errors.OtherReferralDetails}
                          value={data.OtherReferralDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("OtherReferralDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                      <RadioButton
                        name="DeterminationKnown"
                        label={
                          <>
                            1c. Client status: Official determination of
                            trafficking known?
                            <br />
                            <i>
                              [INTERVIEWER: This includes HHS certification,
                              T-visa approval, or certification by law
                              enforcement or a judge]
                            </i>
                          </>
                        }
                        error={errors.DeterminationKnown}
                        value={data.DeterminationKnown["@value"]}
                        options={noYesBrOptions}
                        optionsOnNewLine={true}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <RadioButton
                        name="Sex"
                        label="1d. Sex of client:"
                        error={errors.Sex}
                        value={data.Sex["@value"]}
                        options={sexOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <Input
                        name="Language"
                        label="1e. Language of interview:"
                        error={errors.Language}
                        value={data.Language["@value"]}
                        onChange={handleChange}
                        identical={compare("Language")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <Input
                        name="PreferredLanguage"
                        label="1f. Client's preferred language:"
                        error={errors.PreferredLanguage}
                        value={data.PreferredLanguage["@value"]}
                        onChange={handleChange}
                        identical={compare("PreferredLanguage")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <RadioButton
                        name="EnglishProficiency"
                        label={
                          <>
                            1g. Client's English proficiency{" "}
                            <i>(please estimate to the best of your ability)</i>
                            :
                          </>
                        }
                        error={errors.EnglishProficiency}
                        value={data.EnglishProficiency["@value"]}
                        options={proficiencyOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <RadioButton
                        name="InterviewMode"
                        label="1h. Mode of interview"
                        error={errors.InterviewMode}
                        value={data.InterviewMode["@value"]}
                        options={proficiencyOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="h5 border p-2">
                      Section 2: Personal Background
                    </div>
                    <div className="container">
                      <div
                        className="border p-2 mb-3"
                        style={{ backgroundColor: "rgb(192,192,192)" }}
                      >
                        <b>
                          INTERVIEWER READ: "I'd like to begin by asking you a
                          few simple questions about your personal and family
                          background."
                        </b>
                      </div>
                      <div>
                        2a. What is your date of birth?{" "}
                        {clientData !== undefined &&
                        clientData.client !== undefined &&
                        clientData.client.dob !== undefined
                          ? moment(clientData.client.dob).format("l")
                          : null}
                      </div>
                      <Input
                        name="ApproxAge"
                        label="2b. If you don't know your date of birth, approximately how old are you?"
                        error={errors.ApproxAge}
                        value={data.ApproxAge["@value"]}
                        onChange={handleChange}
                        identical={compare("ApproxAge")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      {data.ApproxAge["@value"] === undefined ||
                      data.ApproxAge["@value"] === "" ? (
                        <RadioButton
                          name="ApproxAgeGroup"
                          label={
                            <>
                              [
                              <i>
                                INTERVIEWER: If respondent cannot provide a
                                number, offer the following response brackets to
                                choose from
                              </i>
                              ]
                            </>
                          }
                          error={errors.ApproxAgeGroup}
                          value={data.ApproxAgeGroup["@value"]}
                          options={ageGroupOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                      <RadioButton
                        name="YearsInSchool"
                        label="2c. How many years of schooling have you completed?"
                        error={errors.YearsInSchool}
                        value={data.YearsInSchool["@value"]}
                        options={yearsInSchoolOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      {data.YearsInSchool["@value"] === undefined ||
                      data.YearsInSchool["@value"] === "Other" ? (
                        <Input
                          name="YearsInSchoolOther"
                          label="If other, how many years?"
                          error={errors.YearsInSchoolOther}
                          value={data.YearsInSchoolOther["@value"]}
                          onChange={handleChange}
                          identical={compare("YearsInSchoolOther")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                      <Input
                        name="CountryBorn"
                        label="2d. What country were you born in?"
                        error={errors.CountryBorn}
                        value={data.CountryBorn["@value"]}
                        onChange={handleChange}
                        identical={compare("CountryBorn")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <RadioButton
                        name="OtherCitizen"
                        label={
                          <>
                            2e. Are you a citizen of any other countries besides
                            where you were born?
                            <br />[
                            <i>
                              INTERVIEWER: If concept of 'citizenship' is not
                              clear, rephrase as 'Where were your parents born?'
                            </i>
                            ]
                          </>
                        }
                        error={errors.OtherCitizen}
                        value={data.OtherCitizen["@value"]}
                        options={noYesDKBrOptions}
                        optionsOnNewLine={true}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <Input
                        name="OtherCitizenCountry"
                        label="Other countries of citizenship:"
                        error={errors.OtherCitizenCountry}
                        value={data.OtherCitizenCountry["@value"]}
                        onChange={handleChange}
                        identical={compare("OtherCitizenCountry")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="h5 d-flex">
                      <span className="me-2">Section 3: Migration</span>
                      <Checkbox
                        name="MigrationNA"
                        label="NA"
                        checked={data.MigrationNA["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3 me-2"
                      />
                      <span>
                        [
                        <i>
                          PLEASE USE THE MIGRATION SECTION WITH FOREIGN-BORN
                          CLIENTS ONLY
                        </i>
                        ]
                      </span>
                    </div>
                    {data.MigrationNA["@value"] !== "true" ? (
                      <div className="container">
                        <div
                          className="border p-2 mb-3"
                          style={{ backgroundColor: "rgb(192,192,192)" }}
                        >
                          <b>
                            INTERVIEWER READ: "Now I am going to ask you some
                            questions about your country of origin. I am not
                            asking you this to find out about your immigration
                            status. I am only trying to understand fully what
                            your circumstances are so that we can refer you for
                            the right help, if necessary. The questions ask
                            about your migration to the U.S., who was involved,
                            and how it was arranged."
                            <br />
                            <br />
                            <i>For children, this may be rephrased</i>: "We
                            would like you to tell us about what happened to you
                            when you traveled to the U.S."
                          </b>
                        </div>
                        <RadioButton
                          name="ReasonForMigration"
                          label="3a. Can you tell me why you left your country?"
                          error={errors.ReasonForMigration}
                          value={data.ReasonForMigration["@value"]}
                          options={reasonForMigrationOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        {data.ReasonForMigration["@value"] === undefined ||
                        data.ReasonForMigration["@value"] === "Other" ? (
                          <Input
                            name="ReasonForMigrationOther"
                            label="If other, please explain:"
                            error={errors.ReasonForMigrationOther}
                            value={data.ReasonForMigrationOther["@value"]}
                            onChange={handleChange}
                            identical={compare("ReasonForMigrationOther")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        ) : null}
                        <Input
                          name="LivePriorUS"
                          label={
                            <>
                              3b. What country did you live in for at least 3
                              months before you came to the U.S.?
                              <br />[
                              <i>
                                INTERVIEWER: If client has come to the U.S. more
                                than once, probe to make sure client refers to
                                most recent place of residence
                              </i>
                              ]
                            </>
                          }
                          error={errors.LivePriorUS}
                          value={data.LivePriorUS["@value"]}
                          onChange={handleChange}
                          identical={compare("LivePriorUS")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <Input
                          name="MostRecentArrival"
                          label="3c. In what year was your most recent arrival to the U.S.?"
                          error={errors.MostRecentArrival}
                          value={data.MostRecentArrival["@value"]}
                          onChange={handleChange}
                          identical={compare("MostRecentArrival")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <Input
                          name="OtherArrivals"
                          label={
                            <>
                              [
                              <i>
                                INTERVIEWER: If client has come to the U.S. more
                                than once, you can ask them about other entries
                                to the U.S. if relevant.
                              </i>
                              ]
                            </>
                          }
                          error={errors.OtherArrivals}
                          value={data.OtherArrivals["@value"]}
                          onChange={handleChange}
                          identical={compare("OtherArrivals")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        {data.MostRecentArrival["@value"] === undefined ||
                        data.MostRecentArrival["@value"] === "" ? (
                          <RadioButton
                            name="MostRecentArrivalApprox"
                            label="If you don't know exactly when you arrived in the U.S., about how long have you been here?"
                            error={errors.MostRecentArrivalApprox}
                            value={data.MostRecentArrivalApprox["@value"]}
                            options={arrivalTimeOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        ) : null}
                        <RadioButton
                          name="ArrangedTravel"
                          label="3d. Did anyone arrange your travel to the U.S.?"
                          error={errors.ArrangedTravel}
                          value={data.ArrangedTravel["@value"]}
                          options={noYesBrOptions}
                          optionsOnNewLine={true}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        {data.ArrangedTravel["@value"] !== "No" ? (
                          <div className="container">
                            <Input
                              name="WhoArrangedTravel"
                              label="Can you tell me who?"
                              error={errors.WhoArrangedTravel}
                              value={data.WhoArrangedTravel["@value"]}
                              onChange={handleChange}
                              identical={compare("WhoArrangedTravel")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                            <Input
                              name="WhatArrangedTravel"
                              label="What did they do?"
                              error={errors.WhatArrangedTravel}
                              value={data.WhatArrangedTravel["@value"]}
                              onChange={handleChange}
                              identical={compare("WhatArrangedTravel")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        ) : null}
                        <RadioButton
                          name="Pressure"
                          label={
                            <>
                              3e. Did the people or person who arranged your
                              travel pressure you to do anything (for example,
                              did anyone ask you to carry something across the
                              border)?
                              <br />
                              <i>
                                [REPHRASE: Did you have to do anything so that
                                they would help you?]
                              </i>
                            </>
                          }
                          error={errors.Pressure}
                          value={data.Pressure["@value"]}
                          options={noYesBrOptions}
                          optionsOnNewLine={true}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        {data.Pressure["@value"] !== "No" ? (
                          <div className="container">
                            <Input
                              name="PressureFor"
                              label="What were you pressured to do?"
                              error={errors.PressureFor}
                              value={data.PressureFor["@value"]}
                              onChange={handleChange}
                              identical={compare("PressureFor")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        ) : null}
                        <RadioButton
                          name="KnowCost"
                          label={
                            <>
                              3f. Can you tell me the total cost (approximately)
                              of your migration?
                              <br />
                              <i>
                                [REPHRASE: : How much did you pay to come to the
                                U.S.?]
                              </i>
                            </>
                          }
                          error={errors.KnowCost}
                          value={data.KnowCost["@value"]}
                          options={noYesBrOptions}
                          optionsOnNewLine={true}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        {data.KnowCost["@value"] !== "No" ? (
                          <div className="container">
                            <Input
                              name="ApproxCost"
                              label="What was the total cost of your migration?"
                              error={errors.ApproxCost}
                              value={data.ApproxCost["@value"]}
                              onChange={handleChange}
                              identical={compare("ApproxCost")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                            <Input
                              name="PaymentCover"
                              label="What did the payment cover (e.g., transportation such as airplane or bus tickets, documents, work placement)?"
                              error={errors.PaymentCover}
                              value={data.PaymentCover["@value"]}
                              onChange={handleChange}
                              identical={compare("PaymentCover")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        ) : null}
                        <RadioButton
                          name="Debt"
                          label={
                            <>
                              3g. Did you (or your family) borrow or owe money,
                              or something else, to anyone who helped you come
                              to the U.S.?
                              <br />[
                              <i>
                                INTERVIEWER: Probe for something else owed, such
                                as property, a house, or land
                              </i>
                              ]
                            </>
                          }
                          error={errors.Debt}
                          value={data.Debt["@value"]}
                          options={noNAYesBrOptions}
                          optionsOnNewLine={true}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        {data.Debt["@value"] !== "No" ? (
                          <div className="container">
                            <RadioButton
                              name="StillDebt"
                              label="Do you (or your family) still have this debt, or does anyone claim you do?"
                              error={errors.StillDebt}
                              value={data.StillDebt["@value"]}
                              options={noYesOptions}
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                            <Input
                              name="DebtDetails"
                              label={[
                                <i>
                                  INTERVIEWER: Record volunteered information
                                  here
                                </i>,
                              ]}
                              error={errors.DebtDetails}
                              value={data.DebtDetails["@value"]}
                              onChange={handleChange}
                              identical={compare("DebtDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        ) : null}
                        <RadioButton
                          name="PressuredCoercion"
                          label="3h. If you did borrow or owe money, have you ever been pressured to do anything you didn't want to do to pay it back?"
                          error={errors.PressuredCoercion}
                          value={data.PressuredCoercion["@value"]}
                          options={noNAYesBrOptions}
                          optionsOnNewLine={true}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        {data.PressuredCoercion["@value"] !== "No" ? (
                          <div className="container">
                            <Input
                              name="PressuredActs"
                              label="If you are comfortable telling me, what kinds of things were you pressured to do that you didn't want to do?"
                              error={errors.PressuredActs}
                              value={data.PressuredActs["@value"]}
                              onChange={handleChange}
                              identical={compare("PressuredActs")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                            <Input
                              name="PressuredHow"
                              label="Could you describe how you were pressured?"
                              error={errors.PressuredHow}
                              value={data.PressuredHow["@value"]}
                              onChange={handleChange}
                              identical={compare("PressuredHow")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        ) : null}
                        <Input
                          name="OtherMigrationDetails"
                          label={
                            <>
                              3i.{" "}
                              <i>
                                INTERVIEWER: If client offered additional
                                information about debts or other victimization
                                related to migration, record it here
                              </i>
                            </>
                          }
                          error={errors.OtherMigrationDetails}
                          value={data.OtherMigrationDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("OtherMigrationDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    ) : null}
                    <div className="h5">Section 4: Work</div>
                    <div
                      className="border p-2 mb-3"
                      style={{ backgroundColor: "rgb(192,192,192)" }}
                    >
                      <b>
                        INTERVIEWER READ: "Now I'm going to ask you some
                        questions about work you've done{" "}
                        <i>
                          <u>in the United States</u>
                        </i>{" "}
                        and people you have worked for and with. I'm
                        particularly interested in any kind of work you've done
                        in which you felt that you did not get paid as much as
                        you should, or if you felt scared or unsafe while
                        working. This includes jobs that were not 'official' in
                        regular workplaces. Remember, everything you tell me is
                        confidential and you do not have to answer any questions
                        that you don't want to answer."
                      </b>
                    </div>
                    <div className="container">
                      <Input
                        name="HowSupportSelf"
                        label={
                          <>
                            4a. How have you supported yourself while in the
                            U.S.?{" "}
                            <i>
                              [REPHRASE: How have you paid for food, housing and
                              other items in the U.S?]
                            </i>
                          </>
                        }
                        error={errors.HowSupportSelf}
                        value={data.HowSupportSelf["@value"]}
                        onChange={handleChange}
                        identical={compare("HowSupportSelf")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <RadioButton
                        name="WorkForPay"
                        label={
                          <>
                            4b. Have you worked for someone or done any other
                            activities for which you thought you would be paid?
                            <br />[
                            <i>
                              INTERVIEWER: This could include activities like
                              unpaid domestic work that might not be readily
                              defined as "work" and should only detail those
                              jobs in which the person felt unsafe or did not
                              get paid what the person felt he/she should.
                            </i>
                            ]
                          </>
                        }
                        error={errors.WorkForPay}
                        value={data.WorkForPay["@value"]}
                        options={noYesBrOptions}
                        optionsOnNewLine={true}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      {data.WorkForPay["@value"] !== "No" ? (
                        <div className="container">
                          <Input
                            name="WorkDetails"
                            label="What kind(s) of work or activities were you doing?"
                            error={errors.WorkDetails}
                            value={data.WorkDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("WorkDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="WorkFound"
                            label={
                              <>
                                How did you find out about these
                                jobs/activities? [
                                <i>
                                  INTERVIEWER: probe for details, especially as
                                  they deal with recruitment from abroad
                                </i>
                                ]
                              </>
                            }
                            error={errors.WorkFound}
                            value={data.WorkFound["@value"]}
                            onChange={handleChange}
                            identical={compare("WorkFound")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      ) : null}
                      <RadioButton
                        name="NoPayment"
                        label={
                          <>
                            4c. Have you ever worked [or done other activities]
                            without getting the payment you thought you would
                            get?
                            <br />[
                            <i>
                              INTERVIEWER: You do not need to say "done other
                              activities" if unnecessary and the client
                              understands work does not just mean formal work.
                            </i>
                            ]
                          </>
                        }
                        error={errors.NoPayment}
                        value={data.NoPayment["@value"]}
                        options={noYesBrOptions}
                        optionsOnNewLine={true}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      {data.NoPayment["@value"] !== "No" ? (
                        <div className="container">
                          <RadioButton
                            name="SameWork"
                            label="Was it the same work as you described above?"
                            error={errors.SameWork}
                            value={data.SameWork["@value"]}
                            options={noYesBrOptions}
                            optionsOnNewLine={true}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          {data.SameWork["@value"] !== "Yes" ? (
                            <Input
                              name="SideWorkDetails"
                              label="What kind(s) of work or activities were you doing?"
                              error={errors.SideWorkDetails}
                              value={data.SideWorkDetails["@value"]}
                              onChange={handleChange}
                              identical={compare("SideWorkDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      ) : null}
                      {data.SameWork["@value"] !== "No" ? (
                        <div className="container">
                          <Input
                            name="SideWorkExpectedPay"
                            label="What payment did you expect and why?"
                            error={errors.SideWorkExpectedPay}
                            value={data.SideWorkExpectedPay["@value"]}
                            onChange={handleChange}
                            identical={compare("SideWorkExpectedPay")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="SideWorkActualPay"
                            label="What did you receive?"
                            error={errors.SideWorkActualPay}
                            value={data.SideWorkActualPay["@value"]}
                            onChange={handleChange}
                            identical={compare("SideWorkActualPay")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      ) : null}
                      4d. Did someone ever (check all that apply):
                      <Checkbox
                        name="WithholdPayment"
                        label="withhold payment/money from you"
                        checked={data.WithholdPayment["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                      <Checkbox
                        name="PaySomeoneElse"
                        label="give your payment/money to someone else"
                        checked={data.PaySomeoneElse["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                      <Checkbox
                        name="ControlPayment"
                        label="control the payment/money that you should have been paid"
                        checked={data.ControlPayment["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                      <Checkbox
                        name="NoPaymentIssues"
                        label="none of the above"
                        checked={data.NoPaymentIssues["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                      <Input
                        name="PaymentIssueDetails"
                        label={
                          <>
                            [
                            <i>
                              INTERVIEWER: Record volunteered information here
                            </i>
                            ]
                          </>
                        }
                        error={errors.PaymentIssueDetails}
                        value={data.PaymentIssueDetails["@value"]}
                        onChange={handleChange}
                        identical={compare("PaymentIssueDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <RadioButton
                        name="SignDocument"
                        label="4e. Were you ever made to sign a document without fully understanding what it stated, for instance, a work contract?"
                        error={errors.SignDocument}
                        value={data.SignDocument["@value"]}
                        options={noYesBrOptions}
                        optionsOnNewLine={true}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      {data.SignDocument["@value"] !== "No" ? (
                        <div className="container">
                          <Input
                            name="SignDocumentDetails"
                            label={
                              <>
                                [<i>INTERVIEWER: Probe for details</i>]
                              </>
                            }
                            error={errors.SignDocumentDetails}
                            value={data.SignDocumentDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("SignDocumentDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      ) : null}
                      <RadioButton
                        name="UnexpectedActivities"
                        label="4f. Have you ever worked [or done other activities] that were different from what you were promised or told?"
                        error={errors.UnexpectedActivities}
                        value={data.UnexpectedActivities["@value"]}
                        options={noYesBrOptions}
                        optionsOnNewLine={true}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      {data.UnexpectedActivities["@value"] !== "No" ? (
                        <div className="container">
                          <Input
                            name="PromisedActivities"
                            label="What were you promised or told that you would do?"
                            error={errors.PromisedActivities}
                            value={data.PromisedActivities["@value"]}
                            onChange={handleChange}
                            identical={compare("PromisedActivities")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="ActualActivities"
                            label="What did you end up doing?"
                            error={errors.ActualActivities}
                            value={data.ActualActivities["@value"]}
                            onChange={handleChange}
                            identical={compare("ActualActivities")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      ) : null}
                      <RadioButton
                        name="FeelUnsafeWork"
                        label={
                          <>
                            4g. Did anyone where you worked [or did other
                            activities] ever make you feel{" "}
                            <u>scared or unsafe</u>?
                          </>
                        }
                        error={errors.FeelUnsafeWork}
                        value={data.FeelUnsafeWork["@value"]}
                        options={noYesBrOptions}
                        optionsOnNewLine={true}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      {data.FeelUnsafeWork["@value"] !== "No" ? (
                        <div className="container">
                          <Input
                            name="UnsafeWorkReason"
                            label="Could you tell me what made you feel scared or unsafe?"
                            error={errors.UnsafeWorkReason}
                            value={data.UnsafeWorkReason["@value"]}
                            onChange={handleChange}
                            identical={compare("UnsafeWorkReason")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      ) : null}
                      <RadioButton
                        name="HarmedYou"
                        label={
                          <>
                            4h. Did anyone where you worked [or did other
                            activities] ever <u>hurt you</u> or{" "}
                            <u>threaten to hurt you</u>?<br />[
                            <i>
                              INTERVIEWER: This could include any physical,
                              sexual, or emotional harm
                            </i>
                            ]
                          </>
                        }
                        error={errors.HarmedYou}
                        value={data.HarmedYou["@value"]}
                        options={noYesBrOptions}
                        optionsOnNewLine={true}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      {data.HarmedYou["@value"] !== "No" ? (
                        <div className="container">
                          <Input
                            name="HarmedYouDetails"
                            label="Could you tell me what they did or said?"
                            error={errors.HarmedYouDetails}
                            value={data.HarmedYouDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("HarmedYouDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      ) : null}
                      <RadioButton
                        name="HarmedOthers"
                        label={
                          <>
                            4i. Did anyone where you worked [or did other
                            activities] ever harm or threaten to harm{" "}
                            <u>people close to you</u>, like family or friends?
                            <br />[
                            <i>
                              INTERVIEWER: This could include any physical,
                              sexual, or emotional harm
                            </i>
                            ]
                          </>
                        }
                        error={errors.HarmedOthers}
                        value={data.HarmedOthers["@value"]}
                        options={noYesBrOptions}
                        optionsOnNewLine={true}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      {data.HarmedOthers["@value"] !== "No" ? (
                        <div className="container">
                          <Input
                            name="HarmedOthersDetails"
                            label="Could you tell me what they did or said?"
                            error={errors.HarmedOthersDetails}
                            value={data.HarmedOthersDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("HarmedOthersDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      ) : null}
                      <RadioButton
                        name="AllowedBreaks"
                        label="4j. Were you ever allowed take breaks where you worked [or did other activities], for example, to eat, use the telephone, or use the bathroom?"
                        error={errors.AllowedBreaks}
                        value={data.AllowedBreaks["@value"]}
                        options={noYesBrOptions}
                        optionsOnNewLine={true}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      {data.AllowedBreaks["@value"] !== "Yes" ? (
                        <div className="container">
                          <Input
                            name="SickTime"
                            label="What if you were sick or had some kind of emergency?"
                            error={errors.SickTime}
                            value={data.SickTime["@value"]}
                            onChange={handleChange}
                            identical={compare("SickTime")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="BreakConsequences"
                            label="What did you think would happen if you took a break?"
                            error={errors.BreakConsequences}
                            value={data.BreakConsequences["@value"]}
                            onChange={handleChange}
                            identical={compare("BreakConsequences")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      ) : null}
                      {data.AllowedBreaks["@value"] !== "No" ? (
                        <div className="container">
                          <Input
                            name="AskForBreak"
                            label="Did you have to ask for permission?"
                            error={errors.AskForBreak}
                            value={data.AskForBreak["@value"]}
                            onChange={handleChange}
                            identical={compare("AskForBreak")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="BreakNoPermission"
                            label="What did you think would happen if you took a break without getting permission?"
                            error={errors.BreakNoPermission}
                            value={data.BreakNoPermission["@value"]}
                            onChange={handleChange}
                            identical={compare("BreakNoPermission")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      ) : null}
                      <RadioButton
                        name="WorkInjury"
                        label="4k. Were you ever injured or did you ever get sick in a place where you worked [or did other activities]?"
                        error={errors.WorkInjury}
                        value={data.WorkInjury["@value"]}
                        options={noYesBrOptions}
                        optionsOnNewLine={true}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      {data.WorkInjury["@value"] !== "No" ? (
                        <div className="container">
                          <RadioButton
                            name="NoMedicalCare"
                            label="Were you ever stopped from getting medical care?"
                            error={errors.NoMedicalCare}
                            value={data.NoMedicalCare["@value"]}
                            options={noYesBrOptions}
                            optionsOnNewLine={true}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="WorkInjuryDetails"
                            label="If you feel comfortable, could you tell me more about what happened?"
                            error={errors.WorkInjuryDetails}
                            value={data.WorkInjuryDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("WorkInjuryDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      ) : null}
                      <Input
                        name="AdditionalWorkDetails"
                        label={
                          <>
                            4l.{" "}
                            <i>
                              INTERVIEWER: if client volunteered additional
                              information relevant to trafficking victimization
                              in a U.S. work context, record it here
                            </i>
                            :
                          </>
                        }
                        error={errors.AdditionalWorkDetails}
                        value={data.AdditionalWorkDetails["@value"]}
                        onChange={handleChange}
                        identical={compare("AdditionalWorkDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="h5">
                      Section 5: Living and/or Working Conditions
                    </div>
                    <div
                      className="border p-2 mb-3"
                      style={{ backgroundColor: "rgb(192,192,192)" }}
                    >
                      <b>
                        INTERVIEWER READ: "Next, I have just a few more
                        questions I'd like to ask about problems you may have
                        had in your living or working situation in the United
                        States."
                      </b>
                    </div>
                    <RadioButton
                      name="LivingSituation"
                      label={
                        <>
                          5a. When you were in that situation, were you living
                          [or do you currently live]?
                          <br />[
                          <i>
                            INTERVIEWER: Should determine if client still in
                            situation in question
                          </i>
                          ]
                        </>
                      }
                      error={errors.LivingSituation}
                      value={data.LivingSituation["@value"]}
                      options={livingSituationOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.LivingSituation["@value"] === undefined ||
                    data.LivingSituation["@value"] === "With others" ? (
                      <Input
                        name="LivingSituationDetails"
                        label="If others, who did you live with?"
                        error={errors.LivingSituationDetails}
                        value={data.LivingSituationDetails["@value"]}
                        onChange={handleChange}
                        identical={compare("LivingSituationDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                    <RadioButton
                      name="LivedAtWork"
                      label={
                        <>
                          5b. Do you live, or have you ever lived, in the same
                          place where you work?
                          <br />[
                          <i>
                            INTERVIEWER: This could include activities like
                            unpaid domestic work that might not be readily
                            defined as "work"
                          </i>
                          ]
                        </>
                      }
                      error={errors.LivedAtWork}
                      value={data.LivedAtWork["@value"]}
                      options={noYesBrOptions}
                      optionsOnNewLine={true}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.LivedAtWork["@value"] !== "No" ? (
                      <div className="container">
                        <Input
                          name="LivedAtWorkDetails"
                          label={
                            <>
                              [
                              <i>
                                INTERVIEWER: Record volunteered information here
                              </i>
                              ]
                            </>
                          }
                          error={errors.LivedAtWorkDetails}
                          value={data.LivedAtWorkDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("LivedAtWorkDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    ) : null}
                    <RadioButton
                      name="UnableToLeave"
                      label={
                        <>
                          5c. Have you ever felt you could not leave the place
                          where you worked [or did other activities]?
                          <br />[
                          <i>
                            INTERVIEWER: Probe for situations where someone
                            threatened to do something bad if client tried to
                            leave.
                          </i>
                          ]
                        </>
                      }
                      error={errors.UnableToLeave}
                      value={data.UnableToLeave["@value"]}
                      options={noYesBrOptions}
                      optionsOnNewLine={true}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.UnableToLeave["@value"] !== "No" ? (
                      <div className="container">
                        <Input
                          name="UnableToLeaveReason"
                          label="Could you tell me why you couldn't leave?"
                          error={errors.UnableToLeaveReason}
                          value={data.UnableToLeaveReason["@value"]}
                          onChange={handleChange}
                          identical={compare("UnableToLeaveReason")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <Input
                          name="LeaveConsequences"
                          label="What do you think would have happened to you if you tried to leave?"
                          error={errors.LeaveConsequences}
                          value={data.LeaveConsequences["@value"]}
                          onChange={handleChange}
                          identical={compare("LeaveConsequences")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    ) : null}
                    <RadioButton
                      name="Locks"
                      label="5d. Have you ever worked [or did other activities] or lived somewhere where there were locks on the doors or windows or anything else that stopped you from leaving?"
                      error={errors.Locks}
                      value={data.Locks["@value"]}
                      options={noYesBrOptions}
                      optionsOnNewLine={true}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.Locks["@value"] !== "No" ? (
                      <div className="container">
                        <Input
                          name="LocksDetails"
                          label={
                            <>
                              [
                              <i>
                                INTERVIEWER: Record volunteered information here
                              </i>
                              ]
                            </>
                          }
                          error={errors.LocksDetails}
                          value={data.LocksDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("LocksDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    ) : null}
                    <RadioButton
                      name="SupervisedContact"
                      label={
                        <>
                          5e. Did anyone at the place where you lived or worked
                          [or did other activities] monitor you or stop you from
                          contacting your family, friends, or others?
                          <br />[
                          <i>
                            REPHRASE: did you have to ask permission to contact
                            your family, friends or others?
                          </i>
                          ]
                        </>
                      }
                      error={errors.SupervisedContact}
                      value={data.SupervisedContact["@value"]}
                      options={noYesBrOptions}
                      optionsOnNewLine={true}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.SupervisedContact["@value"] !== "No" ? (
                      <div className="container">
                        <Input
                          name="SupervisedContactDetails"
                          label="Could you tell me why not?"
                          error={errors.SupervisedContactDetails}
                          value={data.SupervisedContactDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("SupervisedContactDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    ) : null}
                    <RadioButton
                      name="TakeID"
                      label="5f. Did anyone ever take and keep your identification, for example, your passport or driver's license?"
                      error={errors.TakeID}
                      value={data.TakeID["@value"]}
                      options={noYesBrOptions}
                      optionsOnNewLine={true}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.TakeID["@value"] !== "No" ? (
                      <Input
                        name="GetIDBack"
                        label={
                          <>
                            Could you get them back if you wanted? [
                            <i>INTERVIEWER: Probe for details</i>]
                          </>
                        }
                        error={errors.GetIDBack}
                        value={data.GetIDBack["@value"]}
                        onChange={handleChange}
                        identical={compare("GetIDBack")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                    <RadioButton
                      name="FakeDocumentation"
                      label="5g. Did anyone ever force you to get or use false identification or documentation, for example, a fake green card?"
                      error={errors.FakeDocumentation}
                      value={data.FakeDocumentation["@value"]}
                      options={noYesBrOptions}
                      optionsOnNewLine={true}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.FakeDocumentation["@value"] !== "No" ? (
                      <div className="container">
                        <Input
                          name="FakeDocumentationDetails"
                          label={
                            <>
                              [<i>INTERVIEWER: Probe for details</i>]
                            </>
                          }
                          error={errors.FakeDocumentationDetails}
                          value={data.FakeDocumentationDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("FakeDocumentationDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    ) : null}
                    <RadioButton
                      name="WorkLies"
                      label="5h. Did anyone where you worked [or did activities] ever tell you to lie about your age or what you did?"
                      error={errors.WorkLies}
                      value={data.WorkLies["@value"]}
                      options={noYesBrOptions}
                      optionsOnNewLine={true}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.WorkLies["@value"] !== "No" ? (
                      <div className="container">
                        <Input
                          name="WorkLiesDetails"
                          label="Could you explain why they asked you to lie?"
                          error={errors.WorkLiesDetails}
                          value={data.WorkLiesDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("WorkLiesDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    ) : null}
                    <RadioButton
                      name="ReportThreats"
                      label={
                        <>
                          5i. Did anyone you ever worked [or did other
                          activities] for or lived with threaten to report you
                          to the police or other authorities?
                          <br />[
                          <i>
                            INTERVIEWER: If client is foreign-born, probe for
                            threats of being reported to immigration authorities
                          </i>
                          ]
                        </>
                      }
                      error={errors.ReportThreats}
                      value={data.ReportThreats["@value"]}
                      options={noYesBrOptions}
                      optionsOnNewLine={true}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.ReportThreats["@value"] !== "No" ? (
                      <div className="container">
                        <Input
                          name="ReportThreatsDetails"
                          label={
                            <>
                              [<i>INTERVIEWER: Probe for details</i>]
                            </>
                          }
                          error={errors.ReportThreatsDetails}
                          value={data.ReportThreatsDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("ReportThreatsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    ) : null}
                    <RadioButton
                      name="WitnessOtherHarm"
                      label={
                        <>
                          5j. Did you ever see anyone else at the place where
                          you lived or worked [or did other activities] harmed,
                          or threatened with harm?
                          <br />[
                          <i>
                            INTERVIEWER: This can include any physical, sexual,
                            or emotional harm
                          </i>
                          ]
                        </>
                      }
                      error={errors.WitnessOtherHarm}
                      value={data.WitnessOtherHarm["@value"]}
                      options={noYesBrOptions}
                      optionsOnNewLine={true}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.WitnessOtherHarm["@value"] !== "No" ? (
                      <div className="container">
                        <Input
                          name="WitnessOtherHarmDetails"
                          label="If you are comfortable talking about it, could you tell me what happened?"
                          error={errors.WitnessOtherHarmDetails}
                          value={data.WitnessOtherHarmDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("WitnessOtherHarmDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    ) : null}
                    <RadioButton
                      name="WorkPressure"
                      label="5k. Did anyone where you worked [or did other activities] ever trick or pressure you into doing anything you did not want to do?"
                      error={errors.WorkPressure}
                      value={data.WorkPressure["@value"]}
                      options={noYesBrOptions}
                      optionsOnNewLine={true}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.WorkPressure["@value"] !== "No" ? (
                      <div className="container">
                        <Input
                          name="WorkPressureDetails"
                          label="If you are comfortable talking about it, could you please give me some examples?"
                          error={errors.WorkPressureDetails}
                          value={data.WorkPressureDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("WorkPressureDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    ) : null}
                    <RadioButton
                      name="PhysicalPressure"
                      label="5l. Did anyone ever pressure you to touch someone or have any unwanted physical [or sexual] contact with another person?"
                      error={errors.PhysicalPressure}
                      value={data.PhysicalPressure["@value"]}
                      options={noYesBrOptions}
                      optionsOnNewLine={true}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.PhysicalPressure["@value"] !== "No" ? (
                      <div className="container">
                        <Input
                          name="PhysicalPressureDetails"
                          label="If you are comfortable talking about it, could you tell me what happened?"
                          error={errors.PhysicalPressureDetails}
                          value={data.PhysicalPressureDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("PhysicalPressureDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    ) : null}
                    <RadioButton
                      name="UncomfortablePhotos"
                      label="5m. Did anyone ever take a photo of you that you were uncomfortable with?"
                      error={errors.UncomfortablePhotos}
                      value={data.UncomfortablePhotos["@value"]}
                      options={noYesBrOptions}
                      optionsOnNewLine={true}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.UncomfortablePhotos["@value"] !== "No" ? (
                      <div className="container">
                        <Input
                          name="UncomfortablePhotosTakenBy"
                          label="If you feel comfortable talking about this, could you tell me who took the photo?"
                          error={errors.UncomfortablePhotosTakenBy}
                          value={data.UncomfortablePhotosTakenBy["@value"]}
                          onChange={handleChange}
                          identical={compare("UncomfortablePhotosTakenBy")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <Input
                          name="UncomfortablePhotosPlan"
                          label={
                            <>
                              What did they plan to do with the photo, if you
                              know?
                              <br />[
                              <i>
                                LAW ENFORCEMENT: If the respondent indicates
                                that the photo was posted online, you should ask
                                which website.
                              </i>
                              ]
                            </>
                          }
                          error={errors.UncomfortablePhotosPlan}
                          value={data.UncomfortablePhotosPlan["@value"]}
                          onChange={handleChange}
                          identical={compare("UncomfortablePhotosPlan")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <RadioButton
                          name="UncomfortablePhotosAgree"
                          label="Did you agree to this?"
                          error={errors.UncomfortablePhotosAgree}
                          value={data.UncomfortablePhotosAgree["@value"]}
                          options={noYesOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    ) : null}
                    <RadioButton
                      name="SexForValuables"
                      label="5n. Did you ever have sex for things of value (for example money, housing, food, gifts, or favors)?"
                      error={errors.SexForValuables}
                      value={data.SexForValuables["@value"]}
                      options={noYesBrOptions}
                      optionsOnNewLine={true}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.SexForValuables["@value"] !== "No" ? (
                      <div className="container">
                        <Input
                          name="SexForValuablesDetails"
                          label={
                            <>
                              [
                              <i>
                                INTERVIEWER: Probe for any type of sexual
                                activity
                              </i>
                              ]
                            </>
                          }
                          error={errors.SexForValuablesDetails}
                          value={data.SexForValuablesDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("SexForValuablesDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <RadioButton
                          name="SexPressure"
                          label="Were you pressured to do this?"
                          error={errors.SexPressure}
                          value={data.SexPressure["@value"]}
                          options={noYesOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <RadioButton
                          name="SexAsMinor"
                          label="Were you under the age of 18 when this occurred?"
                          error={errors.SexAsMinor}
                          value={data.SexAsMinor["@value"]}
                          options={noYesOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    ) : null}
                    <RadioButton
                      name="TakeMoney"
                      label="5o. Did anyone where you worked [or did other activities] ever take your money for things, for example, for transportation, food, or rent?"
                      error={errors.TakeMoney}
                      value={data.TakeMoney["@value"]}
                      options={noYesBrOptions}
                      optionsOnNewLine={true}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.TakeMoney["@value"] !== "No" ? (
                      <div className="container">
                        <RadioButton
                          name="TakeMoneyAgree"
                          label="Did you agree to this person taking your money?"
                          error={errors.TakeMoneyAgree}
                          value={data.TakeMoneyAgree["@value"]}
                          options={noYesOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <Input
                          name="TakeMoneyDetails"
                          label="Could you describe this situation?"
                          error={errors.TakeMoneyDetails}
                          value={data.TakeMoneyDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("TakeMoneyDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    ) : null}
                    <RadioButton
                      name="RationedFood"
                      label="5p. Did anyone you ever worked [or did other activities] for or lived with control how much food you could get?"
                      error={errors.RationedFood}
                      value={data.RationedFood["@value"]}
                      options={noYesBrOptions}
                      optionsOnNewLine={true}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.RationedFood["@value"] !== "No" ? (
                      <div className="container">
                        <RadioButton
                          name="EnoughFood"
                          label=" Did you get enough food?"
                          error={errors.EnoughFood}
                          value={data.EnoughFood["@value"]}
                          options={noYesOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    ) : null}
                    <RadioButton
                      name="ControlledSleep"
                      label="5q. Did anyone you ever worked [or did other activities] for or lived with control when you could sleep?"
                      error={errors.ControlledSleep}
                      value={data.ControlledSleep["@value"]}
                      options={noYesBrOptions}
                      optionsOnNewLine={true}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.ControlledSleep["@value"] !== "No" ? (
                      <div className="container">
                        <RadioButton
                          name="EnoughSleep"
                          label=" Did you get enough sleep?"
                          error={errors.EnoughSleep}
                          value={data.EnoughSleep["@value"]}
                          options={noYesOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    ) : null}
                    <RadioButton
                      name="LanguageBarriers"
                      label="5r. In this situation, did language difficulties ever prevent you from seeking help when you needed it?"
                      error={errors.LanguageBarriers}
                      value={data.LanguageBarriers["@value"]}
                      options={noYesBrOptions}
                      optionsOnNewLine={true}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <Input
                      name="ConditionDetails"
                      label={
                        <>
                          5s.{" "}
                          <i>
                            INTERVIEWER: if client volunteered additional
                            information relevant to force, fraud or coercion in
                            a work or living situation in the U.S., record it
                            here
                          </i>
                          :
                        </>
                      }
                      error={errors.ConditionDetails}
                      value={data.ConditionDetails["@value"]}
                      onChange={handleChange}
                      identical={compare("ConditionDetails")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <div className="h5">Finishing the Interview</div>[
                    <i>
                      INTERVIEWER: Please tell client what services are
                      available at SAFY.
                    </i>
                    ]
                    <RadioButton
                      name="WantReferral"
                      label="Do you want me to ask someone else at SAFY to get more help for you?"
                      error={errors.WantReferral}
                      value={data.WantReferral["@value"]}
                      options={noYesBrOptions}
                      optionsOnNewLine={true}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <div
                      className="container border p-2 mb-3"
                      style={{ backgroundColor: "rgb(192,192,192)" }}
                    >
                      <b>
                        INTERVIEWER: Tell client the interview is over. Thank
                        the client for their time.
                      </b>
                    </div>
                    <div className="h5">
                      Post-interview Assessment (to be completed by the
                      interviewer)
                    </div>
                    <div className="container">
                      <Input
                        name="NonverbalIndicators"
                        label="6a. Note any nonverbal indicators of past victimization:"
                        error={errors.NonverbalIndicators}
                        value={data.NonverbalIndicators["@value"]}
                        onChange={handleChange}
                        identical={compare("NonverbalIndicators")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <Input
                        name="PotentialInaccuracies"
                        label="6b. Note any indicators that responses may have been inaccurate:"
                        error={errors.PotentialInaccuracies}
                        value={data.PotentialInaccuracies["@value"]}
                        onChange={handleChange}
                        identical={compare("PotentialInaccuracies")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <RadioButton
                        name="Likelihood"
                        label="6c. Indicate the likelihood that the client is a victim of trafficking:"
                        error={errors.Likelihood}
                        value={data.Likelihood["@value"]}
                        options={likelihoodOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <Input
                        name="Reasoning"
                        label="6d. Briefly state your reasoning for your rating:"
                        error={errors.Reasoning}
                        value={data.Reasoning["@value"]}
                        onChange={handleChange}
                        identical={compare("Reasoning")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <Input
                        name="ServicesReferred"
                        label="6e. What kind of service referrals, if any, will you make for the client?"
                        error={errors.ServicesReferred}
                        value={data.ServicesReferred["@value"]}
                        onChange={handleChange}
                        identical={compare("ServicesReferred")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <Input
                        name="AdditionalNotes"
                        label="6f. Additional notes:"
                        error={errors.AdditionalNotes}
                        value={data.AdditionalNotes["@value"]}
                        onChange={handleChange}
                        identical={compare("AdditionalNotes")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </ExpandDiv>
                </>
              ) : null}
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default KYHTSA;
